class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Class cart-product-recommendations extends HTMLElement
class CartProductRecommendations extends HTMLElement {
  connectedCallback() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const intent = 'complementary';
    const locale = window.Shopify && window.Shopify.locale ? window.Shopify.locale : 'en';
    const url = `/recommendations/products.json?product_id=${productId}&intent=${intent}`;

    fetch(url)
      .then(res => res.json())
      .then(data => {
        if (!data.products || !data.products.length) return;
        const container = this;
        // Remove loading state if present
        container.innerHTML = '';

        // Heading
        const heading = document.createElement('h2');
        heading.className = 'related-products__heading inline-richtext';
        heading.textContent = 'You may also like';
        container.appendChild(heading);

        // Slider container
        const sliderDiv = document.createElement('slider-component');
        container.appendChild(sliderDiv);

        // Product grid
        const ul = document.createElement('ul');
        ul.className = 'grid product-grid grid--1-col-desktop grid--1-col-tablet-down simple-slider js-draggable-slider';
        ul.setAttribute('role', 'list');
        ul.setAttribute('data-speed', '3');

        data.products.forEach(product => {
          const li = document.createElement('li');
          li.className = 'grid__item simple-slide';

          // Card markup
          li.innerHTML = `
            <div class="card-product">
              <a href="${product.url}" class="card-product__image-wrapper">
                <img src="${product.featured_image.replace(/^\/\//, 'https://')}" alt="${product.title}" class="card-product__image" loading="lazy" width="300" height="300" style="object-fit:contain;">
              </a>
              <div class="card-product__info">
                <a href="${product.url}" class="card-product__title h4">${product.title}</a>
                <div class="card-product__price">
                  ${product.compare_at_price > product.price
                    ? `<span class="card-product__price--old">${(product.compare_at_price / 100).toLocaleString(undefined, {style:'currency',currency:window.Shopify.currency.active||'USD'})}</span>`
                    : ''
                  }
                  <span class="card-product__price--current">${(product.price / 100).toLocaleString(undefined, {style:'currency',currency:window.Shopify.currency.active||'USD'})}</span>
                </div>
                <a href="${product.url}" class="button button--primary">View Product</a>
              </div>
            </div>
          `;
          ul.appendChild(li);
        });

        sliderDiv.appendChild(ul);
        // Initialize slider
        initDraggableSliders();
        // Removed autoscroll for client request
        // initAutoScrollSliders();
      })
      .catch(() => {});
  }
}

customElements.define('cart-product-recommendations', CartProductRecommendations);